<template>
  <div class="voice2video studio-layout">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>口播转视频配置</span>
          <el-tag type="info" size="small">基于专栏口播方向批量转视频</el-tag>
        </div>
      </template>

      <el-form :model="form" label-width="120px">
        <el-form-item label="主题" required>
          <el-input
            v-model="form.topic"
            type="textarea"
            :rows="3"
            placeholder="例如：AI自动剪辑流程 / 教育博主内容分析"
          />
        </el-form-item>
        <el-form-item label="理论资源">
          <el-input v-model="form.theory" placeholder="可选：本雅明、阿多诺等理论锚点" />
        </el-form-item>
        <el-form-item label="案例素材">
          <el-input v-model="form.cases" placeholder="可选：指定案例对象，便于拆方向更精准" />
        </el-form-item>
        <el-form-item label="补充说明">
          <el-input
            v-model="form.extraHint"
            type="textarea"
            :rows="2"
            placeholder="可选：补充风格、结尾基调、禁用角度等"
          />
        </el-form-item>
        <el-form-item label=" ">
          <el-button
            type="primary"
            :loading="loadingDirections"
            :disabled="!form.topic.trim()"
            @click="fetchDirections"
          >
            AI 拆方向
          </el-button>
          <el-button @click="addCustomDirection">添加自定义方向</el-button>
          <span class="hint">先拆方向，再批量转视频</span>
        </el-form-item>
        <el-form-item label="风格">
          <el-select v-model="form.tone" style="width: 200px">
            <el-option label="专业" value="专业" />
            <el-option label="轻松" value="轻松" />
            <el-option label="情绪感" value="情绪感" />
            <el-option label="故事感" value="故事感" />
          </el-select>
        </el-form-item>
        <el-form-item label="时长策略">
          <el-input-number v-model="form.durationSec" :min="0" :max="7200" />
          <span class="hint">秒；填 0 表示按专栏口播全文自动估算（通常 8–15 分钟）</span>
        </el-form-item>
        <el-form-item>
          <el-button type="primary" :loading="submitting" :disabled="selectedCount === 0" @click="submitTask">
            生成视频任务（{{ selectedCount }} 条）
          </el-button>
          <el-button @click="goTasks">去任务队列</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <el-card class="result-card">
      <template #header>
        <div class="card-header">
          <span>批评方向</span>
          <el-tag size="small">共 {{ directions.length }} 条，已选 {{ selectedCount }} 条</el-tag>
        </div>
      </template>
      <el-table v-if="directions.length" :data="directions" size="small" border stripe>
        <el-table-column width="60" align="center">
          <template #default="{ row }">
            <el-checkbox v-model="row.selected" />
          </template>
        </el-table-column>
        <el-table-column label="#" type="index" width="60" />
        <el-table-column label="方向简称" width="180">
          <template #default="{ row }">
            <el-input v-model="row.label" size="small" />
          </template>
        </el-table-column>
        <el-table-column label="批评核心方向" min-width="320">
          <template #default="{ row }">
            <el-input v-model="row.text" type="textarea" :rows="3" size="small" />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="80" fixed="right">
          <template #default="{ $index }">
            <el-button link type="danger" size="small" @click="removeDirection($index)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <el-empty v-else description="请先填写主题并点击 AI 拆方向，或手动添加方向" />
    </el-card>

    <el-card v-if="job.taskId" class="result-card">
      <template #header>
        <div class="card-header">
          <span>任务进度</span>
          <el-tag :type="job.status === 'completed' ? 'success' : (job.status === 'failed' ? 'danger' : 'warning')">
            {{ job.status || 'pending' }}
          </el-tag>
        </div>
      </template>

      <el-progress :percentage="progress" :stroke-width="10" />
      <p class="meta">任务ID：{{ job.taskId }} ｜ {{ job.done }}/{{ job.total }}</p>

      <el-table v-if="job.scripts.length" :data="job.scripts" size="small" stripe>
        <el-table-column prop="job_index" label="#" width="60" />
        <el-table-column prop="topic" label="主题" min-width="180" />
        <el-table-column prop="script_preview" label="文案预览" min-width="360" />
      </el-table>

      <el-alert
        v-for="(err, idx) in job.errors"
        :key="idx"
        :title="`Job ${err.job_index} - ${err.stage}: ${err.error}`"
        type="error"
        :closable="false"
        show-icon
        class="err"
      />
    </el-card>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage } from 'element-plus'
import { columnOralApi, voice2videoApi } from '../api'

export default {
  name: 'Voice2Video',
  setup() {
    const router = useRouter()
    const submitting = ref(false)
    const timer = ref(null)

    const form = reactive({
      topic: '',
      theory: '',
      cases: '',
      extraHint: '',
      tone: '专业',
      durationSec: 0,
    })
    const loadingDirections = ref(false)
    const directions = ref([])

    const job = reactive({
      taskId: null,
      status: '',
      total: 0,
      done: 0,
      scripts: [],
      errors: [],
    })

    const progress = computed(() => {
      if (!job.total) return 0
      return Math.round((job.done / job.total) * 100)
    })
    const selectedCount = computed(
      () => directions.value.filter((d) => d.selected && (d.text || '').trim()).length,
    )

    const stopPoll = () => {
      if (timer.value) {
        clearTimeout(timer.value)
        timer.value = null
      }
    }

    const poll = async () => {
      if (!job.taskId) return
      try {
        const s = await voice2videoApi.getGenerateStatus(job.taskId)
        job.status = s.status
        job.total = s.total || 0
        job.done = s.done || 0
        job.scripts = s.scripts || []
        job.errors = s.errors || []
        if (['completed', 'failed', 'cancelled'].includes(job.status)) {
          submitting.value = false
          ElMessage.success(`任务结束：${job.status}`)
          stopPoll()
          return
        }
        timer.value = setTimeout(poll, 2000)
      } catch (e) {
        submitting.value = false
        stopPoll()
        ElMessage.error(e?.message || '查询任务失败')
      }
    }

    const fetchDirections = async () => {
      if (!form.topic.trim()) {
        ElMessage.warning('请先填写主题')
        return
      }
      loadingDirections.value = true
      try {
        const res = await columnOralApi.generateDirections({
          topic: form.topic.trim(),
          theory: form.theory.trim(),
          cases: form.cases.trim(),
          extra_hint: form.extraHint.trim(),
        })
        const list = (res.directions || []).map((d, idx) => ({
          id: d.id || `dir_${Date.now()}_${idx}`,
          label: d.label || `方向${idx + 1}`,
          text: d.text || '',
          selected: true,
        }))
        directions.value = list
        ElMessage.success(`已生成 ${list.length} 条方向`)
      } catch (e) {
        ElMessage.error(e?.message || '拆方向失败')
      } finally {
        loadingDirections.value = false
      }
    }

    const addCustomDirection = () => {
      directions.value.push({
        id: `custom_${Date.now()}`,
        label: '自定义方向',
        text: '',
        selected: true,
      })
    }

    const removeDirection = (idx) => {
      directions.value.splice(idx, 1)
    }

    const submitTask = async () => {
      const selected = directions.value.filter((d) => d.selected && (d.text || '').trim())
      if (!selected.length) {
        ElMessage.warning('请至少勾选一条方向并填写内容')
        return
      }
      submitting.value = true
      try {
        const jobs = selected.map((d) => ({
          topic: `${form.topic.trim()}｜${(d.label || '').trim() || '方向'}`,
          tone: form.tone,
          duration_sec: form.durationSec,
          theory: form.theory || '',
          cases: form.cases || '',
          direction: (d.text || '').trim(),
          search_query: (d.search_query || '').trim(),
        }))
        const res = await voice2videoApi.startGenerate({ jobs })
        job.taskId = res.task_id
        job.status = 'pending'
        job.total = res.total || jobs.length
        job.done = 0
        job.scripts = []
        job.errors = []
        ElMessage.success(`任务 #${job.taskId} 已创建`)
        poll()
      } catch (e) {
        submitting.value = false
        ElMessage.error(e?.message || '提交失败')
      }
    }

    const goTasks = () => router.push('/content/tasks')

    onBeforeUnmount(() => stopPoll())

    return {
      form,
      job,
      directions,
      submitting,
      loadingDirections,
      progress,
      selectedCount,
      fetchDirections,
      addCustomDirection,
      removeDirection,
      submitTask,
      goTasks,
    }
  },
}
</script>

<style scoped>
.voice2video {
  padding: 0;
}
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.hint {
  margin-left: 8px;
  color: #909399;
}
.result-card {
  margin-top: 20px;
}
.meta {
  color: #909399;
  font-size: 12px;
}
.err {
  margin-top: 8px;
}
</style>

